import { useState, useMemo, useEffect } from 'react';
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import { doc, getDoc } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";

// Firebase
import { db, auth, GoogleAuthProvider, signInWithPopup, signOut } from './firebaseUtils.js';

// Components
import Layout from './Layout.js';
import Dashboard from './Dashboard.js';
import CreateQuote from './CreateQuote.js';
import CreateInvoice from './CreateInvoice.js';
import Quotes from './Quotes.js';
import Invoices from './Invoices.js';
import QuoteDetail from './QuoteDetail.js';
import InvoiceDetail from './InvoiceDetail.js';
import ProformaInvoice from './ProformaInvoice.js'; 
import Login from './Login.js';
import CreateOrder from './CreateOrder.js';
import Orders from './Orders.js';
import OrderDetail from './OrderDetail.js';
import Spinner from './Spinner.js';
import AccountsDashboard from './AccountsDashboard.js';
import TransactionLedger from './TransactionLedger.js';
import AccountsInvoices from './AccountsInvoices.js';
import AccountsOrders from './AccountsOrders.js';
import CreateMOCPage from './CreateMOCPage.js';
import CreateMICPage from './CreateMICPage.js';
import MaterialMovement from './MaterialMovement.js';
import MaterialInward from './MaterialInward.js';
import ConfirmedOrders from './ConfirmedOrders.js';
import MOCPreview from './MOCPreview.js';
import AccountsApprovals from './AccountsApprovals.js';
import OrderApprovalDetail from './OrderApprovalDetail.js';
import PackingList from './PackingList.js';
import PackingDetail from './PackingDetail.js';
import ReadyToDispatch from './ReadyToDispatch.js';
import DCDetail from './DCDetail.js';
import QualityCheckList from './QualityCheckList.js';
import InboundQCList from './InboundQCList.js';
import QualityCheckDetail from './QualityCheckDetail.js';
import CreateInvTransfer from './CreateInvTransfer.js';
import InventoryTransferList from './InventoryTransferList.js';
import InventoryTransferPreview from './InventoryTransferPreview.js';
import Inventory from './Inventory.js';
import InventoryDetail from './InventoryDetail.js';
import InTransit from './InTransit.js';
import InTransitDetail from './InTransitDetail.js';
import DeliveredList from './DeliveredList.js';
import ReceivedList from './ReceivedList.js';
import DeliveredDetail from './DeliveredDetail.js';
import PickupChallanList from './PickupChallanList.js';
import CreatePickupChallan from './CreatePickupChallan.js';
import PCDetail from './PCDetail.js';
import AdminDashboard from './AdminDashboard.js';
import AdminSettings from './AdminSettings.js';
import AdminProducts from './AdminProducts.js';
import CreateProduct from './CreateProduct.js';
import ProductDetail from './ProductDetail.js';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Persistence and Authorization Check
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        // Double check authorization against Firestore on refresh
        const userData = await checkAuthorization(currentUser.email);
        if (userData) {
          setUser({ ...currentUser, role: userData.role || 'sales' });
        } else {
          await signOut(auth);
          setUser(null);
        }
      } else {
        setUser(null);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const checkAuthorization = async (email) => {
    try {
      const userRef = doc(db, "authorized_users", email);
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        return userSnap.data(); // Return the whole data object containing the role
      }
      return null;
    } catch (error) {
      console.error("Auth check failed:", error);
      return null;
    }
  };

  const handleLogin = async () => {
    const provider = new GoogleAuthProvider();
    try {
      const result = await signInWithPopup(auth, provider);
      const loggedInUser = result.user;

      const userData = await checkAuthorization(loggedInUser.email);

      if (userData) {
        setUser({ ...loggedInUser, role: userData.role || 'sales' });
      } else {
        await signOut(auth);
        alert("Access Denied: Your email is not whitelisted in the system.");
      }
    } catch (error) {
      console.error("Login failed:", error);
      alert("Login failed. Please try again.");
    }
  };

  const handleLogout = () => {
    signOut(auth).then(() => setUser(null));
  };

  const router = useMemo(() => createBrowserRouter([
    {
      path: "/",
      element: <Layout user={user} handleLogout={handleLogout} db={db} />,
      children: [
        { path: "/", element: user?.role === 'accounts' ? <AccountsDashboard db={db} user={user} /> : (user?.role === 'admin' ? <AdminDashboard db={db} user={user} /> : <Dashboard db={db} user={user} />) },
        // Admin routes
        { path: "/admin", element: user?.role === 'admin' ? <AdminDashboard db={db} /> : <Navigate to="/" /> },
        { path: "/admin/products", element: user?.role === 'admin' ? <AdminProducts db={db} user={user} /> : <Navigate to="/" /> },
        { path: "/admin/products/:id", element: user?.role === 'admin' ? <ProductDetail db={db} user={user} /> : <Navigate to="/" /> },
        { path: "/admin/products/new", element: user?.role === 'admin' ? <CreateProduct db={db} user={user} /> : <Navigate to="/" /> },
        { path: "/admin/products/edit/:id", element: user?.role === 'admin' ? <CreateProduct db={db} user={user} /> : <Navigate to="/" /> },
        { path: "/admin/settings", element: user?.role === 'admin' ? <AdminSettings db={db} /> : <Navigate to="/" /> },
        // Sales-only routes
        { path: "/quotes/new", element: user?.role === 'sales' ? <CreateQuote db={db} /> : <Navigate to="/" /> },
        { path: "/quotes", element: user?.role === 'sales' ? <Quotes db={db} /> : <Navigate to="/" /> },
        { path: "/quotes/:id", element: user?.role === 'sales' ? <QuoteDetail db={db} /> : <Navigate to="/" /> },
        { path: "/quotes/:id/pi", element: user?.role === 'sales' ? <ProformaInvoice db={db} /> : <Navigate to="/" /> },
        { path: "/invoices/new", element: user?.role === 'sales' ? <CreateInvoice db={db} /> : <Navigate to="/" /> },
        { path: "/invoices", element: user?.role === 'sales' ? <Invoices db={db} /> : <Navigate to="/" /> },
        { path: "/invoices/:id", element: user?.role === 'sales' ? <InvoiceDetail db={db} /> : <Navigate to="/" /> },
        { path: "/orders/new", element: user?.role === 'sales' ? <CreateOrder db={db} user={user} /> : <Navigate to="/" /> },
        // New Ops Workflow Routes
        { path: "/ops/moc/new", element: user?.role === 'ops' ? <CreateMOCPage db={db} /> : <Navigate to="/" /> },
        { path: "/ops/moc/edit/:mocId", element: user?.role === 'ops' ? <CreateMOCPage db={db} /> : <Navigate to="/" /> },
        { path: "/ops/moc/preview/:mocId", element: user?.role === 'ops' ? <MOCPreview db={db} /> : <Navigate to="/" /> },
        { path: "/ops/mic/new", element: user?.role === 'ops' ? <CreateMICPage db={db} /> : <Navigate to="/" /> },
        { path: "/ops/mic/edit/:micId", element: user?.role === 'ops' ? <CreateMICPage db={db} /> : <Navigate to="/" /> },
        { path: "/ops/shipments", element: user?.role === 'ops' ? <MaterialMovement db={db} /> : <Navigate to="/" /> },
        { path: "/ops/mic", element: user?.role === 'ops' ? <MaterialInward db={db} /> : <Navigate to="/" /> },
        { path: "/ops/packing", element: user?.role === 'ops' ? <PackingList db={db} user={user} /> : <Navigate to="/" /> },
        { path: "/ops/packing/:id", element: user?.role === 'ops' ? <PackingDetail db={db} /> : <Navigate to="/" /> },
        { path: "/ops/ready-to-dispatch", element: user?.role === 'ops' ? <ReadyToDispatch db={db} user={user} /> : <Navigate to="/" /> },
        { path: "/ops/dispatch/:id", element: ['ops', 'accounts'].includes(user?.role) ? <DCDetail db={db} user={user} /> : <Navigate to="/" /> },
        { path: "/ops/quality-check", element: user?.role === 'ops' ? <QualityCheckList db={db} user={user} /> : <Navigate to="/" /> },
        { path: "/ops/inbound-qc", element: user?.role === 'ops' ? <InboundQCList db={db} user={user} /> : <Navigate to="/" /> },
        { path: "/ops/qc/:id", element: user?.role === 'ops' ? <QualityCheckDetail db={db} /> : <Navigate to="/" /> },
        { path: "/ops/in-transit", element: user?.role === 'ops' ? <InTransit db={db} user={user} /> : <Navigate to="/" /> },
        { path: "/ops/in-transit/:id", element: user?.role === 'ops' ? <InTransitDetail db={db} /> : <Navigate to="/" /> },
        { path: "/ops/delivered", element: user?.role === 'ops' ? <DeliveredList db={db} user={user} /> : <Navigate to="/" /> },
        { path: "/ops/received", element: user?.role === 'ops' ? <ReceivedList db={db} user={user} /> : <Navigate to="/" /> },
        { path: "/ops/delivered/:id", element: user?.role === 'ops' ? <DeliveredDetail db={db} /> : <Navigate to="/" /> },
        { path: "/ops/pickup-challan", element: user?.role === 'ops' ? <PickupChallanList db={db} user={user} /> : <Navigate to="/" /> },
        { path: "/ops/pickup-challan/new", element: user?.role === 'ops' ? <CreatePickupChallan db={db} user={user} /> : <Navigate to="/" /> },
        { path: "/ops/pickup-challan/:id", element: user?.role === 'ops' ? <PCDetail db={db} user={user} /> : <Navigate to="/" /> },
        { path: "/ops/pickup-challan/edit/:id", element: user?.role === 'ops' ? <CreatePickupChallan db={db} user={user} /> : <Navigate to="/" /> },
        { path: "/ops/inventory-transfer", element: user?.role === 'ops' ? <InventoryTransferList db={db} user={user} /> : <Navigate to="/" /> },
        { path: "/ops/inventory", element: user?.role === 'ops' ? <Inventory db={db} user={user} /> : <Navigate to="/" /> },
        { path: "/ops/inventory/:id", element: (user?.role === 'ops' || user?.role === 'admin') ? <InventoryDetail db={db} user={user} /> : <Navigate to="/" /> },
        { path: "/ops/inventory-transfer/:id", element: user?.role === 'ops' ? <InventoryTransferPreview db={db} user={user} /> : <Navigate to="/" /> },
        { path: "/ops/inventory-transfer/new", element: user?.role === 'ops' ? <CreateInvTransfer db={db} user={user} /> : <Navigate to="/" /> },
        { path: "/ops/inventory-transfer/edit/:id", element: user?.role === 'ops' ? <CreateInvTransfer db={db} user={user} /> : <Navigate to="/" /> },
        // Accounts-only specific module routes (dashboard handled by root path)
        { path: "/accounts/transactions", element: user?.role === 'accounts' ? <TransactionLedger db={db} /> : <Navigate to="/" /> },
        { path: "/accounts/invoices", element: user?.role === 'accounts' ? <AccountsInvoices db={db} /> : <Navigate to="/" /> },
        { path: "/accounts/orders", element: user?.role === 'accounts' ? <AccountsOrders db={db} user={user} /> : <Navigate to="/" /> },
        { path: "/accounts/approvals", element: user?.role === 'accounts' ? <AccountsApprovals db={db} user={user} /> : <Navigate to="/" /> },
        { path: "/accounts/approvals/:orderId", element: user?.role === 'accounts' ? <OrderApprovalDetail db={db} user={user} /> : <Navigate to="/" /> },
        // Common routes (Order Module has its own internal role filtering)
        { path: "/orders", element: ['sales', 'ops'].includes(user?.role) ? <Orders db={db} user={user} /> : (user?.role === 'accounts' ? <Navigate to="/accounts/orders" /> : <Navigate to="/" />) },
        { path: "/orders/confirmed", element: ['sales', 'ops'].includes(user?.role) ? <ConfirmedOrders db={db} user={user} /> : <Navigate to="/" /> },
        { path: "/orders/:id", element: user?.role === 'accounts' ? <Navigate to="/accounts/orders" /> : <OrderDetail db={db} user={user} /> },
        { path: "*", element: <Navigate to="/" /> },
      ],
    },
  ]), [user, handleLogout]);

  if (loading) {
    return <div style={{ height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}><Spinner /></div>;
  }

  // 1. LOGIN SCREEN (If not logged in)
  if (!user) {
    return <Login handleLogin={handleLogin} />;
  }

  // 2. APP SCREEN (If logged in)
  return <RouterProvider router={router} />;
}

export default App;